import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { createAvenant, updateAvenant, createPricedAvenant } from "../api/avenants"

const blankState = () => ({
  title:           "",
  description:     "",
  amount_euros:    "",
  signed_date:     "",
  reason:          "",
  delay_days:      "",
  project_task_id: "",
})

const toEuros = cents => (cents == null ? "" : (cents / 100).toString().replace(".", ","))
const toCents = value => {
  if (value == null || value === "") return 0
  const normalized = String(value).replace(/\s/g, "").replace(",", ".")
  const n = parseFloat(normalized)
  if (isNaN(n)) return 0
  return Math.round(n * 100)
}

const FIELD = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
const LABEL = "block text-xs font-medium text-slate-700 mb-1.5"

const AvenantModal = ({ isOpen, mode, projectId, avenant, lots = [], reasons = [], onClose, onSaved }) => {
  const [form,   setForm]   = useState(blankState())
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState([])

  const priced    = mode !== "edit"
  const lumpSum   = mode === "edit" && avenant && !avenant.priced

  useEffect(() => {
    if (!isOpen) return
    if (mode === "edit" && avenant) {
      setForm({
        title:           avenant.title        || "",
        description:     avenant.description  || "",
        amount_euros:    toEuros(avenant.amount?.cents),
        signed_date:     avenant.signed_date  || "",
        reason:          avenant.reason       || "",
        delay_days:      avenant.delay_days ? String(avenant.delay_days) : "",
        project_task_id: avenant.lot?.id ? String(avenant.lot.id) : "",
      })
    } else {
      setForm(blankState())
    }
    setErrors([])
  }, [isOpen, mode, avenant])

  const handleSave = () => {
    if (saving) return
    setSaving(true)
    setErrors([])

    const body = {
      title:           form.title,
      description:     form.description,
      reason:          form.reason || null,
      delay_days:      form.delay_days === "" ? 0 : Number(form.delay_days),
      project_task_id: form.project_task_id || null,
    }
    if (lumpSum) body.amount_cents = toCents(form.amount_euros)
    if (mode === "edit") body.signed_date = form.signed_date || null

    const request = priced
      ? createPricedAvenant(projectId, body)
      : (mode === "edit" && avenant)
        ? updateAvenant(projectId, avenant.id, body)
        : createAvenant(projectId, body)

    request
      .then(response => {
        if (response && response.errors) {
          setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
          return
        }
        if (priced && response?.builder_url) {
          window.location.href = response.builder_url
          return
        }
        onSaved && onSaved(response)
        onClose()
      })
      .catch(() => setErrors([I18n.t("projects.avenants.errors.server")]))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("projects.avenants.form.cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !form.title.trim()}>
          <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
          {mode === "edit"
            ? I18n.t("projects.avenants.form.save")
            : I18n.t("projects.avenants.form.create_priced")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {mode === "edit"
        ? I18n.t("projects.avenants.form.eyebrow_edit", { number: avenant?.number || "" })
        : I18n.t("projects.avenants.form.eyebrow_new")}
      title   = {mode === "edit"
        ? (avenant?.title || I18n.t("projects.avenants.form.title_fallback"))
        : I18n.t("projects.avenants.form.title_new")}
      footer  = {footer}
    >
      {priced && (
        <div className="mb-4 px-3 py-2 rounded-md bg-blue-50 border border-blue-100 text-[12.5px] text-blue-800">
          {I18n.t("projects.avenants.form.priced_hint")}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className={lumpSum ? "col-span-8" : "col-span-12"}>
          <label className={LABEL}>
            {I18n.t("projects.avenants.form.field_title")} <span className="text-rose-500">*</span>
          </label>
          <input
            value       = {form.title}
            onChange    = {e => setForm({ ...form, title: e.target.value })}
            placeholder = {I18n.t("projects.avenants.form.title_placeholder")}
            className   = {FIELD}
          />
        </div>

        {lumpSum && (
          <div className="col-span-4">
            <label className={LABEL}>
              {I18n.t("projects.avenants.form.field_amount")} <span className="text-rose-500">*</span>
            </label>
            <input
              value       = {form.amount_euros}
              onChange    = {e => setForm({ ...form, amount_euros: e.target.value })}
              placeholder = "0,00"
              className   = {`${FIELD} text-right tabular-nums`}
            />
            <p className="text-[11px] text-slate-500 mt-1">{I18n.t("projects.avenants.form.amount_hint")}</p>
          </div>
        )}

        <div className="col-span-6">
          <label className={LABEL}>{I18n.t("projects.avenants.form.field_reason")}</label>
          <select
            value     = {form.reason}
            onChange  = {e => setForm({ ...form, reason: e.target.value })}
            className = {FIELD}
          >
            <option value="">{I18n.t("projects.avenants.form.reason_placeholder")}</option>
            {reasons.map(reason => (
              <option key={reason.value} value={reason.value}>{reason.label}</option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 mt-1">{I18n.t("projects.avenants.form.reason_hint")}</p>
        </div>

        <div className="col-span-6">
          <label className={LABEL}>{I18n.t("projects.avenants.form.field_lot")}</label>
          <select
            value     = {form.project_task_id}
            onChange  = {e => setForm({ ...form, project_task_id: e.target.value })}
            className = {FIELD}
            disabled  = {lots.length === 0}
          >
            <option value="">{I18n.t("projects.avenants.form.lot_placeholder")}</option>
            {lots.map(lot => (
              <option key={lot.id} value={lot.id}>{lot.label}</option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 mt-1">{I18n.t("projects.avenants.form.lot_hint")}</p>
        </div>

        <div className="col-span-12">
          <label className={LABEL}>{I18n.t("projects.avenants.form.field_description")}</label>
          <textarea
            value       = {form.description}
            onChange    = {e => setForm({ ...form, description: e.target.value })}
            rows        = "4"
            placeholder = {I18n.t("projects.avenants.form.description_placeholder")}
            className   = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-4">
          <label className={LABEL}>{I18n.t("projects.avenants.form.field_delay")}</label>
          <input
            type      = "number"
            min       = "0"
            value     = {form.delay_days}
            onChange  = {e => setForm({ ...form, delay_days: e.target.value })}
            placeholder = "0"
            className = {`${FIELD} text-right tabular-nums`}
          />
          <p className="text-[11px] text-slate-500 mt-1">{I18n.t("projects.avenants.form.delay_hint")}</p>
        </div>

        {mode === "edit" && (
          <div className="col-span-4">
            <label className={LABEL}>{I18n.t("projects.avenants.form.field_signed_date")}</label>
            <input
              type      = "date"
              value     = {form.signed_date}
              onChange  = {e => setForm({ ...form, signed_date: e.target.value })}
              className = {FIELD}
            />
            <p className="text-[11px] text-slate-500 mt-1">{I18n.t("projects.avenants.form.signed_date_hint")}</p>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}
    </Modal>
  )
}

export default AvenantModal
